import type { Custodian } from 'models/custodian';
import type {
  IErrorMessage,
  IProductSearchResult,
  IQuoteRequest,
  IQuoteResponse,
  ITradeRequest,
  ITradeResponse,
} from 'models/secondaryMarket';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import type { SecondaryPriceType, SecondaryQuotationType } from 'state/secondaryMarket/productSearch';
import type { AstroProduct } from 'state/secondaryMarket/referential/astroProduct/astroProduct.model';
import type { IHttpClient } from 'utils/http/httpClient.model';

type LegacyQuote = {
  correlationId: string;
  accruedCoupon: number;
  cashAmount: number;
  quotationType: SecondaryQuotationType;
  deductedFee: string;
  price: number;
  priceType: SecondaryPriceType;
  alphaLogin: string;
  settlementPrice: number;
  upfrontFee: number;
  timeToTradeInSeconds: number;
  tradeDate: string;
  quoteId: string;
  eventDate: string;
  settlementDate: string;
};
export const apiConnector = (http$: IHttpClient) => ({
  loadAstroProduct(isin: string): Observable<AstroProduct | 'not_found'> {
    return http$
      .getJSON<IProductSearchResult>({
        url: `api/v2/secondary/products/${isin}`,
        expect404NotFound: true,
        targetHost: 'SecondaryBridge',
      })
      .pipe(
        map(({ payload }) => {
          if (payload === undefined) {
            return 'not_found';
          }
          return payload as AstroProduct;
        }),
      );
  },
  loadAstroClient(bdrId: string): Observable<Custodian[] | 'not_found'> {
    return http$
      .getJSON<{ custodians: Custodian[] | undefined }>({
        url: `api/v1/secondary/clients/${bdrId}`,
        expect404NotFound: true,
        targetHost: 'SecondaryBridge',
      })
      .pipe(
        map(({ payload }) => {
          if (payload === undefined) {
            return 'not_found';
          }
          return payload.custodians ?? [];
        }),
      );
  },
  astroQuote(quoteRequest: IQuoteRequest): Observable<{ data: LegacyQuote; errors: IErrorMessage[] | undefined }> {
    return http$
      .postJSON<IQuoteResponse>({
        url: 'api/v1/secondary/quote/request',
        body: quoteRequest,
        timeOut: 60_000,
        tryParseErrorPayload: true,
        useTextErrorPayload: true,
        targetHost: 'SecondaryBridge',
      })
      .pipe(
        map(({ payload }) => payload),
        map(({ errors, ...quote }) => {
          const data: LegacyQuote = {
            correlationId: quote.correlationId,
            accruedCoupon: quote.accruedCoupon,
            cashAmount: quote.cashAmount,
            quotationType: quote.quotationType as SecondaryQuotationType,
            deductedFee: quote.deductedFee,
            price: quote.price,
            priceType: quote.priceType as SecondaryPriceType,
            alphaLogin: quote.alphaLogin,
            settlementPrice: quote.settlementPrice,
            upfrontFee: quote.upfrontFee,
            timeToTradeInSeconds: quote.timeToTradeInSeconds,
            tradeDate: quote.tradeDate,
            quoteId: quote.quoteId,
            eventDate: quote.eventDate,
            settlementDate: quote.settlementDate,
          };

          return {
            data,
            errors,
          };
        }),
      );
  },
  astroTrade(tradeRequest: ITradeRequest): Observable<ITradeResponse> {
    return http$
      .postJSON<ITradeResponse>({
        url: 'api/v1/secondary/quote/trade',
        body: tradeRequest,
        timeOut: 60_000,
        tryParseErrorPayload: true,
        useTextErrorPayload: true,
        targetHost: 'SecondaryBridge',
      })
      .pipe(map(({ payload }) => payload));
  },
});
